<template>
  <div class="agenda-page">
    <!-- Header moderno -->
    <div class="modern-header">
      <div class="header-container">
        <!-- Breadcrumb e navegação -->
        <div class="breadcrumb-section">
          <nav class="breadcrumb">
            <button @click="$router.push('/dashboard')" class="breadcrumb-item">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
              {{ t('agenda.breadcrumb.dashboard') }}
            </button>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">{{ t('agenda.breadcrumb.current') }}</span>
          </nav>
        </div>

        <!-- Título e informações principais -->
        <div class="title-section">
          <div class="title-content">
            <div class="title-icon">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
            <div class="title-text">
              <h1 class="main-title">{{ t('agenda.title') }}</h1>
              <p class="subtitle">{{ t('agenda.subtitle') }}</p>
            </div>
          </div>
          
          <!-- Badge do tipo de agenda -->
          <div class="workspace-badge type-agenda">
            <div class="badge-icon">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
            <span class="badge-text">{{ t('agenda.badge') }}</span>
          </div>
        </div>

        <!-- Ações do header -->
        <div class="actions-section">
          <!-- Conectar Google Agenda -->
          <button 
            v-if="!googleConnected"
            @click="connectGoogleCalendar"
            class="action-button secondary"
            :disabled="loading"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
            </svg>
            {{ t('agenda.connectGoogle') }}
          </button>
          
          <!-- Sincronizar -->
          <button 
            v-if="googleConnected"
            @click="syncEvents"
            class="action-button secondary"
            :disabled="loading"
          >
            <svg v-if="!loading" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            <svg v-else class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            {{ loading ? t('agenda.syncing') : t('agenda.sync') }}
          </button>
          
          <!-- Desconectar Google -->
          <button 
            v-if="googleConnected"
            @click="disconnectGoogle"
            class="action-button danger"
            :disabled="loading"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
            {{ t('agenda.disconnect') }}
          </button>
          
        </div>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filters-section">
      <div class="filters-left">
        <div class="search-box">
          <svg class="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          <input 
            v-model="searchTerm" 
            type="text" 
            :placeholder="t('agenda.searchPlaceholder')" 
            class="search-input"
          />
        </div>
      </div>
      
      <div class="filters-right">
        <select v-model="selectedUser" class="filter-select">
          <option value="">{{ t('agenda.filterAllUsers') }}</option>
          <option v-for="user in users" :key="user.id" :value="user.id">
            {{ user.nomeCompleto }}
          </option>
        </select>
        
        <select v-model="selectedWorkspace" class="filter-select">
          <option value="">{{ t('agenda.filterAllWorkspaces') }}</option>
          <option v-for="workspace in workspaces" :key="workspace.id" :value="workspace.id">
            {{ workspace.nome }}
          </option>
        </select>
        
        <select v-model="selectedSource" class="filter-select">
          <option value="">{{ t('agenda.filterAllSources') }}</option>
          <option value="BACKLOG">{{ t('agenda.sourceBacklog') }}</option>
          <option value="GOOGLE">{{ t('agenda.sourceGoogle') }}</option>
        </select>
      </div>
    </div>

    <!-- Conteúdo Principal -->
    <div class="agenda-content">
      <!-- Coluna do Calendário -->
      <div class="calendar-column">
        <div class="calendar-container">
          <div class="calendar-header">
            <button @click="previousMonth" class="calendar-nav-btn">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
            
            <h2 class="calendar-title">
              {{ currentMonthName }} {{ currentYear }}
            </h2>
            
            <button @click="nextMonth" class="calendar-nav-btn">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
          
          <!-- Legenda dos Indicadores -->
          <div class="calendar-legend">
            <div class="legend-item">
              <div class="legend-indicator google"></div>
              <span class="legend-text">{{ t('agenda.legend.googleCalendar') }}</span>
            </div>
            <div class="legend-item">
              <div class="legend-indicator task"></div>
              <span class="legend-text">{{ t('agenda.legend.taskSchedules') }}</span>
            </div>
            <div class="legend-item">
              <div class="legend-indicator lead"></div>
              <span class="legend-text">{{ t('agenda.legend.leadSchedules') }}</span>
            </div>
          </div>
          
          <div class="calendar-grid">
            <!-- Dias da semana -->
            <div class="calendar-weekdays">
              <div v-for="dayKey in weekdayKeys" :key="dayKey" class="weekday">{{ t(`agenda.weekdays.${dayKey}`) }}</div>
            </div>
            
            <!-- Dias do mês -->
            <div class="calendar-days">
              <div 
                v-for="day in calendarDays" 
                :key="day.date"
                @click="selectDate(day.date)"
                :class="['calendar-day', {
                  'other-month': !day.isCurrentMonth,
                  'today': day.isToday,
                  'selected': day.isSelected,
                  'has-events': day.eventCount > 0
                }]"
              >
                <span class="day-number">{{ day.day }}</span>
                <div v-if="day.eventCount > 0" class="event-indicators">
                  <div 
                    v-if="day.googleCount > 0" 
                    class="event-indicator google"
                    :title="t('agenda.calendar.googleEventsTitle', { count: day.googleCount })"
                  >
                    <span class="event-count">{{ day.googleCount }}</span>
                  </div>
                  <div 
                    v-if="day.taskCount > 0" 
                    class="event-indicator task"
                    :title="t('agenda.calendar.taskEventsTitle', { count: day.taskCount })"
                  >
                    <span class="event-count">{{ day.taskCount }}</span>
                  </div>
                  <div 
                    v-if="day.leadCount > 0" 
                    class="event-indicator lead"
                    :title="t('agenda.calendar.leadEventsTitle', { count: day.leadCount })"
                  >
                    <span class="event-count">{{ day.leadCount }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Coluna da Lista de Eventos -->
      <div class="events-column">
        <div class="events-container">
          <div class="events-header">
            <h3 class="events-title">
              {{ eventsPanelTitle }}
            </h3>
            <div class="events-stats">
              <span class="stat-item">
                <span class="stat-value">{{ filteredEvents.length }}</span>
                <span class="stat-label">{{ t('agenda.events.countLabel') }}</span>
              </span>
            </div>
          </div>
          
          <!-- Grid de Eventos no Estilo Workspace -->
          <div class="events-grid">
            <div 
              v-for="evento in filteredEvents" 
              :key="evento.id"
              class="event-card"
              @click="openEventDetails(evento)"
            >
              <!-- Card Header Premium -->
              <div class="card-header-premium">
                <div class="event-identity">
                  <div class="event-avatar" :class="evento.fonte.toLowerCase()">
                    <img v-if="evento.fonte === 'GOOGLE'" src="/Google__G__logo.png" alt="Google" class="avatar-icon">
                    <svg v-else class="avatar-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                    </svg>
                  </div>
                  <div class="event-details">
                    <div class="event-title">{{ evento.fonte === 'GOOGLE' ? t('agenda.events.googleAgenda') : t('agenda.events.backlog') }}</div>
                    <div class="event-badge" :class="evento.fonte.toLowerCase()">{{ evento.fonte }}</div>
                  </div>
                </div>
                <button 
                  v-if="evento.fonte === 'BACKLOG' && (evento.leadId || evento.taskId)"
                  @click.stop="openEditModal(evento)"
                  class="action-btn edit"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                </button>
              </div>
              
              <!-- Event Content -->
              <div class="event-content">
                <h3 class="event-title-main">{{ evento.titulo }}</h3>
                <p v-if="evento.descricao" class="event-description">{{ evento.descricao }}</p>
                
                <!-- Event Metrics -->
                <div class="event-metrics">
                  <div class="metric-item">
                    <div class="metric-icon">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <div class="metric-content">
                      <div class="metric-label">{{ t('agenda.events.time') }}</div>
                      <div class="metric-value">{{ formatTime(evento.dataInicio) }} - {{ formatTime(evento.dataFim) }}</div>
                    </div>
                  </div>
                  
                  <div v-if="evento.localizacao" class="metric-item">
                    <div class="metric-icon">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                    </div>
                    <div class="metric-content">
                      <div class="metric-label">{{ t('agenda.events.location') }}</div>
                      <div class="metric-value">{{ evento.localizacao }}</div>
                    </div>
                  </div>
                  
                  <div class="metric-item">
                    <div class="metric-icon">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    </div>
                    <div class="metric-content">
                      <div class="metric-label">{{ t('agenda.events.responsible') }}</div>
                      <div class="metric-value">{{ evento.usuarioNome }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-if="filteredEvents.length === 0" class="no-events">
              <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <p>{{ t('agenda.events.empty') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes do Evento Google -->
    <div v-if="showGoogleEventModal" class="modal-overlay" @click="showGoogleEventModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ t('agenda.modal.title') }}</h3>
          <button @click="showGoogleEventModal = false" class="modal-close">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <div v-if="selectedEvent" class="modal-body">
          <div class="event-detail-header">
            <div class="event-source-detail">
              <img src="/google-logo.svg" alt="Google" class="source-logo-large"/>
              <span class="source-label-large">{{ t('agenda.modal.googleCalendar') }}</span>
            </div>
          </div>
          
          <div class="event-detail-content">
            <h4 class="event-detail-title">{{ selectedEvent.titulo }}</h4>
            <p v-if="selectedEvent.descricao" class="event-detail-description">{{ selectedEvent.descricao }}</p>
            
            <div class="event-detail-info">
              <div class="detail-item">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <div>
                  <span class="detail-label">{{ t('agenda.modal.time') }}</span>
                  <span class="detail-value">{{ formatDateTime(selectedEvent.dataInicio) }} - {{ formatDateTime(selectedEvent.dataFim) }}</span>
                </div>
              </div>
              
              <div v-if="selectedEvent.localizacao" class="detail-item">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <div>
                  <span class="detail-label">{{ t('agenda.modal.location') }}</span>
                  <span class="detail-value">{{ selectedEvent.localizacao }}</span>
                </div>
              </div>
              
              <div class="detail-item">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
                <div>
                  <span class="detail-label">{{ t('agenda.modal.organizer') }}</span>
                  <span class="detail-value">{{ selectedEvent.usuarioNome }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useGlobalWorkspaces } from '@/composables/useGlobalWorkspaces'
import api from '@/services/api'
import Swal from 'sweetalert2'

const { t, locale } = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const weekdayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})
const { workspaces } = useGlobalWorkspaces()

// Estado da página
const loading = ref(false)
const searchTerm = ref('')
const selectedUser = ref('')
const selectedWorkspace = ref('')
const selectedSource = ref('')
const selectedDate = ref(null)
const showGoogleEventModal = ref(false)
const selectedEvent = ref(null)

// Estado do Google Calendar
const googleConnected = ref(false)
const googleIntegration = ref(null)

// Estado do calendário
const currentDate = ref(new Date())
const currentYear = ref(new Date().getFullYear())
const currentMonth = ref(new Date().getMonth())

// Estado dos eventos
const eventos = ref([])
const users = ref([])
const eventosPorDia = ref({})
const eventosPorDiaPorFonte = ref({})

// Computed
const currentMonthName = computed(() => {
  return t(`agenda.months.${currentMonth.value}`)
})

const eventsPanelTitle = computed(() => {
  if (selectedDate.value) {
    return t('agenda.events.titleForDate', { date: formatDate(selectedDate.value) })
  }
  return t('agenda.events.selectDate')
})

const calendarDays = computed(() => {
  const year = currentYear.value
  const month = currentMonth.value
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startDate = new Date(firstDay)
  startDate.setDate(startDate.getDate() - firstDay.getDay())
  
  const days = []
  const today = new Date()
  
  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)
    
    const dateStr = date.toISOString().split('T')[0]
    
    // Buscar contadores por fonte
    const eventosDoDia = eventosPorDiaPorFonte.value[dateStr] || {}
    const googleCount = eventosDoDia['GOOGLE'] || 0
    const taskCount = eventosDoDia['TASK'] || 0 // Tasks são TASK
    const leadCount = eventosDoDia['LEAD'] || 0 // Leads são LEAD
    const totalCount = googleCount + taskCount + leadCount
    
    // Log para debug
    if (totalCount > 0) {
      console.log(`📊 [AgendaPage] ${dateStr}: Google=${googleCount}, Task=${taskCount}, Lead=${leadCount}, Total=${totalCount}`)
    }
    
    days.push({
      date: dateStr,
      day: date.getDate(),
      isCurrentMonth: date.getMonth() === month,
      isToday: date.toDateString() === today.toDateString(),
      isSelected: selectedDate.value === dateStr,
      eventCount: totalCount,
      googleCount: googleCount,
      taskCount: taskCount,
      leadCount: leadCount
    })
  }
  
  return days
})

const filteredEvents = computed(() => {
  let filtered = eventos.value

  // Filtro por data selecionada
  if (selectedDate.value) {
    filtered = filtered.filter(evento => {
      let eventDate
      // Se dataInicio é um array [ano, mês, dia, hora, minuto]
      if (Array.isArray(evento.dataInicio)) {
        const [ano, mes, dia] = evento.dataInicio
        eventDate = new Date(ano, mes - 1, dia).toISOString().split('T')[0]
      } else {
        eventDate = new Date(evento.dataInicio).toISOString().split('T')[0]
      }
      return eventDate === selectedDate.value
    })
  }

  // Filtro por termo de busca
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase()
    filtered = filtered.filter(evento => 
      evento.titulo.toLowerCase().includes(term) ||
      evento.descricao?.toLowerCase().includes(term) ||
      evento.usuarioNome.toLowerCase().includes(term)
    )
  }

  // Filtro por usuário
  if (selectedUser.value) {
    filtered = filtered.filter(evento => evento.usuarioId === parseInt(selectedUser.value))
  }

  // Filtro por workspace (implementar quando necessário)
  if (selectedWorkspace.value) {
    // filtered = filtered.filter(evento => evento.workspaceId === parseInt(selectedWorkspace.value))
  }

  // Filtro por fonte
  if (selectedSource.value) {
    filtered = filtered.filter(evento => evento.fonte === selectedSource.value)
  }

  // Ordenar por horário
  return filtered.sort((a, b) => {
    let dateA, dateB
    
    // Se dataInicio é um array [ano, mês, dia, hora, minuto]
    if (Array.isArray(a.dataInicio)) {
      const [anoA, mesA, diaA, horaA, minutoA] = a.dataInicio
      dateA = new Date(anoA, mesA - 1, diaA, horaA, minutoA)
    } else {
      dateA = new Date(a.dataInicio)
    }
    
    if (Array.isArray(b.dataInicio)) {
      const [anoB, mesB, diaB, horaB, minutoB] = b.dataInicio
      dateB = new Date(anoB, mesB - 1, diaB, horaB, minutoB)
    } else {
      dateB = new Date(b.dataInicio)
    }
    
    return dateA - dateB
  })
})

// Métodos
const loadEventos = async () => {
  try {
    loading.value = true
    // Usar o endpoint que busca agendamentos existentes
    const response = await api.get('/eventos-agenda/filtrados')
    eventos.value = response.data
    console.log('Eventos carregados:', response.data)
  } catch (error) {
    console.error('Erro ao carregar eventos:', error)
  } finally {
    loading.value = false
  }
}

const loadEventosPorDia = async () => {
  try {
    const startDate = new Date(currentYear.value, currentMonth.value, 1)
    const endDate = new Date(currentYear.value, currentMonth.value + 1, 0)
    
    const response = await api.get('/eventos-agenda/calendario', {
      params: {
        dataInicio: startDate.toISOString().split('T')[0],
        dataFim: endDate.toISOString().split('T')[0]
      }
    })
    
    eventosPorDia.value = response.data
  } catch (error) {
    console.error('Erro ao carregar eventos por dia:', error)
  }
}

const loadEventosPorDiaPorFonte = async () => {
  try {
    console.log('🔄 [AgendaPage] Carregando eventos por dia por fonte...')
    const startDate = new Date(currentYear.value, currentMonth.value, 1)
    const endDate = new Date(currentYear.value, currentMonth.value + 1, 0)
    
    console.log('📅 [AgendaPage] Período:', startDate.toISOString().split('T')[0], 'até', endDate.toISOString().split('T')[0])
    
    const response = await api.get('/eventos-agenda/calendario-detalhado', {
      params: {
        dataInicio: startDate.toISOString().split('T')[0],
        dataFim: endDate.toISOString().split('T')[0]
      }
    })
    
    eventosPorDiaPorFonte.value = response.data
    console.log('📊 [AgendaPage] Eventos por dia por fonte carregados:', response.data)
  } catch (error) {
    console.error('❌ [AgendaPage] Erro ao carregar eventos por dia por fonte:', error)
  }
}

const loadUsers = async () => {
  try {
    // Implementar quando necessário
    users.value = []
  } catch (error) {
    console.error('Erro ao carregar usuários:', error)
  }
}

const checkGoogleConnection = async () => {
  try {
    console.log('🔍 Verificando conexão Google para usuário:', authStore.user.id)
    const response = await api.get(`/google-calendar/integration/${authStore.user.id}`)
    console.log('✅ Integração Google encontrada:', response.data)
    googleConnected.value = true
    googleIntegration.value = response.data
  } catch (error) {
    console.log('❌ Integração Google não encontrada:', error.response?.status)
    googleConnected.value = false
    googleIntegration.value = null
  }
}

const connectGoogleCalendar = async () => {
  try {
    loading.value = true
    const response = await api.post('/google-calendar/auth-url', {
      usuarioId: authStore.user.id
    })
    
    console.log('🔗 Redirecionando para Google:', response.data.authUrl)
    // Redireciona para o Google
    window.location.href = response.data.authUrl
  } catch (error) {
    console.error('Erro ao conectar Google Calendar:', error)
    Swal.fire({
      title: t('agenda.swal.error'),
      text: t('agenda.swal.connectError', {
        detail: error.response?.data?.message || error.message
      }),
      icon: 'error',
      confirmButtonText: t('agenda.swal.ok')
    })
  } finally {
    loading.value = false
  }
}

const syncEvents = async () => {
  try {
    loading.value = true
    console.log('🔄 Iniciando sincronização...')
    
    let googleSyncSuccess = false
    let backlogSyncSuccess = false
    
    // Sincronizar eventos do Google para o Backlog
    if (googleConnected.value) {
      try {
        console.log('📥 Sincronizando eventos do Google...')
        await api.post(`/google-calendar/sync-from-google/${authStore.user.id}`)
        console.log('✅ Eventos do Google sincronizados')
        googleSyncSuccess = true
      } catch (googleError) {
        console.error('❌ Erro na sincronização do Google:', googleError)
        const errorMessage = googleError.response?.data?.message || googleError.message
        
        if (errorMessage.includes('Token de acesso inválido') || errorMessage.includes('401')) {
          Swal.fire({
            title: t('agenda.swal.tokenExpiredTitle'),
            text: t('agenda.swal.tokenExpiredText'),
            icon: 'warning',
            confirmButtonText: t('agenda.swal.ok')
          })
          googleConnected.value = false
        } else {
          Swal.fire({
            title: t('agenda.swal.error'),
            text: t('agenda.swal.syncGoogleError', { detail: errorMessage }),
            icon: 'error',
            confirmButtonText: t('agenda.swal.ok')
          })
        }
      }
    }
    
    // Sincronizar eventos do Backlog para o Google
    if (googleConnected.value) {
      try {
        console.log('📤 Sincronizando eventos do Backlog...')
        await api.post(`/google-calendar/sync-to-google/${authStore.user.id}`)
        console.log('✅ Eventos do Backlog sincronizados')
        backlogSyncSuccess = true
      } catch (backlogError) {
        console.error('❌ Erro na sincronização do Backlog:', backlogError)
        // Não mostrar erro para sincronização do Backlog para o Google
      }
    }
    
    // Recarregar eventos
    console.log('🔄 Recarregando eventos...')
    await loadEventos()
    await loadEventosPorDia()
    await loadEventosPorDiaPorFonte()
    console.log('✅ Eventos recarregados')
    
    // Mostrar mensagem de sucesso
    if (googleConnected.value && googleSyncSuccess) {
      Swal.fire({
        title: t('agenda.swal.success'),
        text: t('agenda.swal.syncSuccess'),
        icon: 'success',
        confirmButtonText: t('agenda.swal.ok'),
        timer: 3000,
        showConfirmButton: false
      })
    } else if (googleConnected.value) {
      Swal.fire({
        title: t('agenda.swal.attention'),
        text: t('agenda.swal.syncPartial'),
        icon: 'warning',
        confirmButtonText: t('agenda.swal.ok')
      })
    } else {
      Swal.fire({
        title: t('agenda.swal.success'),
        text: t('agenda.swal.syncBacklogOnly'),
        icon: 'success',
        confirmButtonText: t('agenda.swal.ok'),
        timer: 3000,
        showConfirmButton: false
      })
    }
    
  } catch (error) {
    console.error('❌ Erro geral na sincronização:', error)
    Swal.fire({
      title: t('agenda.swal.error'),
      text: t('agenda.swal.syncEventsError', {
        detail: error.response?.data?.message || error.message
      }),
      icon: 'error',
      confirmButtonText: t('agenda.swal.ok')
    })
  } finally {
    loading.value = false
  }
}

const disconnectGoogle = async () => {
  try {
    loading.value = true
    await api.post(`/google-calendar/disconnect/${authStore.user.id}`)
    googleConnected.value = false
    googleIntegration.value = null
    Swal.fire({
      title: t('agenda.swal.success'),
      text: t('agenda.swal.disconnectSuccess'),
      icon: 'success',
      confirmButtonText: t('agenda.swal.ok'),
      timer: 3000,
      showConfirmButton: false
    })
  } catch (error) {
    console.error('Erro ao desconectar Google:', error)
    Swal.fire({
      title: t('agenda.swal.error'),
      text: t('agenda.swal.disconnectError', {
        detail: error.response?.data?.message || error.message
      }),
      icon: 'error',
      confirmButtonText: t('agenda.swal.ok')
    })
  } finally {
    loading.value = false
  }
}


const loadEventosDoDia = async (date) => {
  try {
    console.log('📅 Carregando eventos para o dia:', date)
    // Filtrar eventos do dia selecionado
    const eventosDoDia = eventos.value.filter(evento => {
      let eventoDate
      // Se dataInicio é um array [ano, mês, dia, hora, minuto]
      if (Array.isArray(evento.dataInicio)) {
        const [ano, mes, dia] = evento.dataInicio
        eventoDate = new Date(ano, mes - 1, dia)
      } else {
        eventoDate = new Date(evento.dataInicio)
      }
      const selectedDate = new Date(date)
      return eventoDate.toDateString() === selectedDate.toDateString()
    })
    
    console.log('📊 [AgendaPage] Eventos encontrados para o dia:', eventosDoDia.length)
    console.log('📋 [AgendaPage] Detalhes dos eventos:', eventosDoDia.map(e => ({
      titulo: e.titulo,
      fonte: e.fonte,
      dataInicio: e.dataInicio
    })))
    // Os eventos já estão carregados em eventos.value, só precisamos filtrar
  } catch (error) {
    console.error('Erro ao carregar eventos do dia:', error)
  }
}

const selectDate = (date) => {
  selectedDate.value = date
  // Carregar eventos do dia selecionado
  loadEventosDoDia(date)
}

const previousMonth = () => {
  currentMonth.value--
  if (currentMonth.value < 0) {
    currentMonth.value = 11
    currentYear.value--
  }
  loadEventosPorDia()
  loadEventosPorDiaPorFonte()
}

const nextMonth = () => {
  currentMonth.value++
  if (currentMonth.value > 11) {
    currentMonth.value = 0
    currentYear.value++
  }
  loadEventosPorDia()
  loadEventosPorDiaPorFonte()
}

const openEventDetails = (evento) => {
  if (evento.fonte === 'GOOGLE') {
    selectedEvent.value = evento
    showGoogleEventModal.value = true
  } else if (evento.leadId || evento.taskId) {
    openEditModal(evento)
  }
}

const openEditModal = (evento) => {
  if (evento.leadId) {
    router.push(`/workspace/${evento.workspaceId}/lead/${evento.leadId}`)
  } else if (evento.taskId) {
    router.push(`/workspace/${evento.workspaceId}/task/${evento.taskId}`)
  }
}

const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString(dateLocale.value, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatTime = (dateTime) => {
  if (Array.isArray(dateTime)) {
    const [ano, mes, dia, hora, minuto] = dateTime
    const date = new Date(ano, mes - 1, dia, hora, minuto)
    return date.toLocaleTimeString(dateLocale.value, {
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  const date = new Date(dateTime)
  return date.toLocaleTimeString(dateLocale.value, {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatDateTime = (dateTime) => {
  if (Array.isArray(dateTime)) {
    const [ano, mes, dia, hora, minuto] = dateTime
    const date = new Date(ano, mes - 1, dia, hora, minuto)
    return date.toLocaleString(dateLocale.value, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  const date = new Date(dateTime)
  return date.toLocaleString(dateLocale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Watchers
watch([selectedUser, selectedWorkspace, selectedSource], () => {
  // Recarregar eventos quando filtros mudarem
})

// Watcher para recarregar após conexão Google
watch(googleConnected, (newValue) => {
  if (newValue) {
    console.log('🔄 Google conectado, recarregando eventos...')
    loadEventos()
    loadEventosPorDia()
    loadEventosPorDiaPorFonte()
  }
})

// Verificar se veio do callback do Google
const checkForGoogleCallback = () => {
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('code') && urlParams.get('state')) {
    console.log('🔄 Detectado callback do Google, aguardando processamento...')
    // Limpar URL
    window.history.replaceState({}, document.title, window.location.pathname)
    // Aguardar um pouco e verificar conexão
    setTimeout(() => {
      checkGoogleConnection()
    }, 2000)
  }
}

// Lifecycle
onMounted(async () => {
  // Verificar callback do Google primeiro
  checkForGoogleCallback()
  
  await Promise.all([
    loadEventos(),
    loadEventosPorDia(),
    loadEventosPorDiaPorFonte(),
    loadUsers(),
    checkGoogleConnection()
  ])
})
</script>

<style scoped>
.agenda-page {
  width: 100%;
  min-height: calc(100vh - 64px);
  background-color: var(--bg-primary);
}

/* Header moderno */
.modern-header {
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-primary);
  padding: 24px 0;
  margin-bottom: 32px;
}

.header-container {
  width: 100%;
  padding: 0 24px;
}

/* Breadcrumb */
.breadcrumb-section {
  margin-bottom: 16px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
  font-size: 14px;
}

.breadcrumb-item:hover {
  color: var(--primary);
}

.breadcrumb-separator {
  color: var(--text-tertiary);
  font-size: 12px;
}

.breadcrumb-current {
  color: var(--text-primary);
  font-weight: 500;
}

/* Título e informações principais */
.title-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.title-icon {
  width: 48px;
  height: 48px;
  border-radius: 4px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.title-text {
  flex: 1;
}

.main-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px 0;
  line-height: 1.2;
}

.subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.4;
}

/* Badge do workspace */
.workspace-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid;
  transition: all 0.2s ease;
}

.workspace-badge.type-agenda {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-color: #10b981;
}

.badge-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.badge-text {
  font-weight: 600;
}

/* Ações do header */
.actions-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid;
  text-decoration: none;
}

.action-button.secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border-color: var(--border-primary);
}

.action-button.secondary:hover {
  background: var(--bg-tertiary);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.action-button.danger {
  background: #ef4444;
  color: white;
  border-color: #ef4444;
}

.action-button.danger:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* Filtros */
.filters-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px 24px;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  width: 100%;
}

.filters-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  width: 16px;
  height: 16px;
  color: var(--text-secondary);
  pointer-events: none;
}

.search-input {
  padding: 10px 12px 10px 40px;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  width: 300px;
  transition: border-color 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary);
}

.filters-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
}

/* Conteúdo Principal */
.agenda-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding: 0 24px;
  margin-bottom: 32px;
}

/* Coluna do Calendário */
.calendar-column {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 24px;
}

.calendar-container {
  width: 100%;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

/* Legenda dos Indicadores */
.calendar-legend {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 20px;
  padding: 12px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  border: 1px solid var(--border-primary);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.legend-indicator.google {
  background: linear-gradient(135deg, #4285f4 0%, #1a73e8 100%);
}

.legend-indicator.task {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.legend-indicator.lead {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.legend-text {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  white-space: nowrap;
}

.calendar-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.calendar-nav-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--primary);
}

.calendar-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.calendar-grid {
  width: 100%;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
}

.weekday {
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  padding: 8px 4px;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.calendar-day {
  position: relative;
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--bg-primary);
  border: 1px solid transparent;
}

.calendar-day:hover {
  background: var(--bg-tertiary);
  border-color: var(--primary);
}

.calendar-day.other-month {
  color: var(--text-tertiary);
  background: transparent;
}

.calendar-day.today {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

.calendar-day.selected {
  background: var(--primary-dark);
  color: white;
  border-color: var(--primary-dark);
}

.calendar-day.has-events {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  color: white;
  border-color: var(--primary);
}

.day-number {
  font-size: 14px;
  font-weight: 500;
}

.event-indicators {
  position: absolute;
  top: 3px;
  right: 3px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-end;
  z-index: 10;
}

.event-indicator {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 700;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
  transition: all 0.2s ease;
}

.event-indicator:hover {
  transform: scale(1.1);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
}

.event-indicator.google {
  background: linear-gradient(135deg, #4285f4 0%, #1a73e8 100%);
  border-color: rgba(66, 133, 244, 0.3);
}

.event-indicator.task {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-color: rgba(16, 185, 129, 0.3);
}

.event-indicator.lead {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-color: rgba(245, 158, 11, 0.3);
}

.event-count {
  font-size: 8px;
  font-weight: 700;
  color: white;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

/* Coluna da Lista de Eventos */
.events-column {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 24px;
}

/* Grid de Eventos no Estilo Workspace */
.events-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.event-card {
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.event-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary) 0%, var(--primary-dark) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.event-card:hover {
  transform: translateY(-4px) scale(1.01);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  border-color: var(--primary);
}

.event-card:hover::before {
  opacity: 1;
}

/* Card Header Premium */
.card-header-premium {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.event-identity {
  display: flex;
  align-items: center;
  gap: 12px;
}

.event-avatar {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.event-avatar.google {
  background: transparent;
  padding: 4px;
}

.event-avatar.backlog {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.avatar-icon {
  width: 20px;
  height: 20px;
  color: white;
}

.event-avatar.google .avatar-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 0;
}

.event-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.event-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.event-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.event-badge.google {
  background: rgba(66, 133, 244, 0.1);
  color: #4285f4;
  border: 1px solid rgba(66, 133, 244, 0.2);
}

.event-badge.backlog {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--primary);
  color: var(--primary);
}

.action-btn.edit:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6;
  color: #3b82f6;
}

/* Event Content */
.event-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.event-title-main {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.4;
  margin: 0;
}

.event-description {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0;
}

/* Event Metrics */
.event-metrics {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.metric-icon {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: var(--bg-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  flex-shrink: 0;
}

.metric-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.metric-label {
  font-size: 10px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1.3;
}

.events-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.events-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-primary);
}

.events-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.events-stats {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.stat-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--primary);
}

.stat-label {
  font-size: 12px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.events-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.event-item {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.event-item:hover {
  border-color: var(--primary);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.event-item.event-google {
  border-left: 4px solid #4285f4;
}

.event-item.event-backlog {
  border-left: 4px solid var(--primary);
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.event-source {
  display: flex;
  align-items: center;
  gap: 8px;
}

.source-logo {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.backlog-logo {
  background: var(--primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.source-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.event-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border-color: var(--primary);
}

.event-content {
  flex: 1;
}

.event-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.event-description {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.event-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.event-time,
.event-location,
.event-user {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-secondary);
}

.no-events {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: var(--text-secondary);
  text-align: center;
}

.no-events svg {
  margin-bottom: 16px;
  opacity: 0.5;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.event-detail-header {
  margin-bottom: 24px;
}

.event-source-detail {
  display: flex;
  align-items: center;
  gap: 12px;
}

.source-logo-large {
  width: 32px;
  height: 32px;
  border-radius: 4px;
}

.source-label-large {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.event-detail-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 12px 0;
}

.event-detail-description {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.event-detail-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.detail-item svg {
  color: var(--text-secondary);
  margin-top: 2px;
}

.detail-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.detail-value {
  display: block;
  font-size: 14px;
  color: var(--text-primary);
}

/* Animações */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Responsividade */
@media (max-width: 768px) {
  .header-container {
    padding: 0 16px;
  }
  
  .title-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .actions-section {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    width: 100%;
  }
  
  .filters-section {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }
  
  .filters-right {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    width: 100%;
  }
  
  .agenda-content {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 0 16px;
  }
  
  .calendar-column,
  .events-column {
    padding: 16px;
  }
  
  .events-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .event-card {
    padding: 16px;
  }
  
  .event-avatar {
    width: 36px;
    height: 36px;
  }
  
  .avatar-icon {
    width: 18px;
    height: 18px;
  }
  
  .event-avatar.google .avatar-icon {
    width: 28px;
    height: 28px;
  }
  
  .calendar-legend {
    flex-wrap: wrap;
    gap: 12px;
    padding: 10px;
  }
  
  .legend-text {
    font-size: 10px;
  }
  
  .main-title {
    font-size: 24px;
  }
  
  .subtitle {
    font-size: 14px;
  }
}
</style>